// Shared by every 牌桌 game's page (public/cards-<kind>.js) and the panel (public/cards.js): the card pictures
// (cards.png, drawn by tools/cards/draw-cards.mjs) and the pieces each game draws its felt with: the felt, seats
// round it with name boxes, chips, a clock bar, fitting a name into a box. No DOM besides <canvas>; no game rules.
import { t } from '/i18n.js';

// ---- the cards' pictures (tools/cards/draw-cards.mjs: rows s h d c, columns 2..A; row 4 the back, 小王, 大王)
export const CARD_W = 24, CARD_H = 34;
const RANKS = '23456789TJQKA', SUITS = 'shdc';
const sheet = new Image(); sheet.src = new URL('./cards.png', import.meta.url).pathname;
/** Resolves once the card sheet has loaded (or failed: cards are then plain boxes). */
export const cardsReady = new Promise((ok) => { sheet.onload = ok; sheet.onerror = ok; });
/** Where card `c` is in the sheet, [sx, sy]: "As" (rank 23456789TJQKA, suit s h d c), "back", "BJ" (小王), "RJ" (大王). */
export function cardRect(c) {
  if (c === 'BJ') return [CARD_W, 4 * CARD_H];
  if (c === 'RJ') return [2 * CARD_W, 4 * CARD_H];
  if (typeof c !== 'string' || c.length !== 2 || !RANKS.includes(c[0]) || !SUITS.includes(c[1])) return [0, 4 * CARD_H]; // the back
  return [RANKS.indexOf(c[0]) * CARD_W, SUITS.indexOf(c[1]) * CARD_H];
}
/** Draw card `c` with its top-left at (x, y), `k` times its pixel size (whole numbers keep it crisp; 1.5 is fine);
 *  dim: greyed out (not part of the winning hand, folded). */
export function drawCard(g, c, x, y, k = 1, dim = false) {
  const [sx, sy] = cardRect(c);
  g.imageSmoothingEnabled = false;
  g.globalAlpha = dim ? 0.45 : 1;
  if (sheet.complete && sheet.naturalWidth) g.drawImage(sheet, sx, sy, CARD_W, CARD_H, Math.round(x), Math.round(y), CARD_W * k, CARD_H * k);
  else { g.fillStyle = '#fffdf6'; g.fillRect(x, y, CARD_W * k, CARD_H * k); g.strokeStyle = '#1b1410'; g.strokeRect(x, y, CARD_W * k, CARD_H * k); }
  g.globalAlpha = 1;
}
/** A card in words: 黑桃A, 红桃10, 小王 … */
export const cardName = (c) => (c === 'BJ' || c === 'RJ' ? t(`cards.card.${c}`) : t('cards.card.named', { suit: SUITS.includes(c[1]) ? t(`cards.card.suit.${c[1]}`) : '', rank: c[0] === 'T' ? '10' : c[0] }));

// ---- drawing the table
/** A seat's name: a 补位 bot's (id "bot:<seat>") in the page's words (the office sends 补位 3), else the person's. */
export const seatName = (p, i) => (!p ? null : typeof p.id === 'string' && p.id.startsWith('bot:') ? t('cards.bot', { n: i + 1 }) : p.name);
export const INK = '#1b1410', CREAM = '#fff8e7', YELLOW = '#FDD03B', WIN = '#bbf7d0';
/** A canvas sized for a w × h CSS-pixel drawing at the screen's density; returns its 2D context, transformed. */
export function sized(canvas, w, h) {
  const dpr = Math.max(1, Math.min(3, devicePixelRatio || 1));
  if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) { canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr); }
  const g = canvas.getContext('2d');
  g.setTransform(dpr, 0, 0, dpr, 0, 0); g.clearRect(0, 0, w, h);
  return g;
}
export function rr(g, x, y, w, h, r) { g.beginPath(); g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r); g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath(); }
/** The oval felt with its wooden rim, filling a w × h drawing. */
export function drawFelt(g, w, h) {
  g.fillStyle = '#7a4a22'; g.beginPath(); g.ellipse(w / 2, h / 2, w * 0.39, h * 0.33, 0, 0, 7); g.fill();
  g.strokeStyle = INK; g.lineWidth = 3; g.stroke();
  g.fillStyle = '#2f7d4a'; g.beginPath(); g.ellipse(w / 2, h / 2, w * 0.36, h * 0.293, 0, 0, 7); g.fill();
  g.strokeStyle = '#1f5a35'; g.lineWidth = 2; g.stroke();
}
/** Where seat i of n goes on a w × h drawing (the middle of its name box): round an oval, the seat `bottom` (mine, or
 *  0 for a watcher) at the bottom, then clockwise, as the map has them (public/cardseats.js). */
export function seatXY(i, n, bottom, w, h) {
  const a = Math.PI / 2 + ((i - bottom) * 2 * Math.PI) / n;
  return [w / 2 + Math.cos(a) * w * 0.375, h / 2 + Math.sin(a) * h * 0.386];
}
export const BOX_W = 84, BOX_H = 34;
/** `text` cut to fit `w` px (with …). */
export function fit(g, text, w) { if (g.measureText(text).width <= w) return text; let t = text; while (t.length > 1 && g.measureText(t + '…').width > w) t = t.slice(0, -1); return t + '…'; }
/**
 * A seat's name box at (x, y): o.name (null: an empty seat, showing o.empty), o.line (the second line: chips, 地主, …),
 * o.mine (thick outline), o.acting (yellow), o.won (green), o.dim (folded, waiting), o.clock (0..1: the move clock left,
 * a bar along the bottom), o.badge (a letter in a disc at the top right: D for the dealer).
 */
export function nameBox(g, x, y, o) {
  g.globalAlpha = o.name == null ? 0.75 : o.dim ? 0.55 : 1;
  rr(g, x - BOX_W / 2, y - BOX_H / 2, BOX_W, BOX_H, 8);
  g.fillStyle = o.name == null ? 'rgba(255,248,231,.5)' : o.acting ? YELLOW : o.won ? WIN : CREAM; g.fill();
  g.strokeStyle = INK; g.lineWidth = o.mine ? 3 : 2; g.setLineDash(o.name == null ? [4, 3] : []); g.stroke(); g.setLineDash([]);
  g.fillStyle = INK; g.textAlign = 'center'; g.textBaseline = 'middle';
  if (o.name == null) { g.font = '12px system-ui'; g.fillText(o.empty ?? t('cards.seat.empty'), x, y); g.globalAlpha = 1; return; }
  g.font = 'bold 12px system-ui'; g.fillText(fit(g, o.name, BOX_W - 10), x, o.line ? y - 7 : y);
  if (o.line) { g.font = '12px system-ui'; g.fillText(fit(g, o.line, BOX_W - 6), x, y + 8); }
  g.globalAlpha = 1;
  if (o.badge) { const dx = x + BOX_W / 2 - 4, dy = y - BOX_H / 2 + 2; g.fillStyle = '#fffdf6'; g.beginPath(); g.arc(dx, dy, 7, 0, 7); g.fill(); g.strokeStyle = INK; g.lineWidth = 1.5; g.stroke(); g.fillStyle = INK; g.font = 'bold 9px system-ui'; g.fillText(o.badge, dx, dy + 0.5); }
  if (o.clock != null) {
    const f = Math.max(0, Math.min(1, o.clock));
    g.fillStyle = 'rgba(27,20,16,.25)'; g.fillRect(x - BOX_W / 2 + 6, y + BOX_H / 2 - 5, BOX_W - 12, 3);
    g.fillStyle = f < 0.3 ? '#dc2626' : '#15803d'; g.fillRect(x - BOX_W / 2 + 6, y + BOX_H / 2 - 5, (BOX_W - 12) * f, 3);
  }
}
/** Which seat's name box is under a pointer event on a w × h drawing (seat positions from at(i)), or -1. */
export function boxAt(canvas, e, w, h, n, at) {
  const r = canvas.getBoundingClientRect(), x = ((e.clientX - r.left) / r.width) * w, y = ((e.clientY - r.top) / r.height) * h;
  for (let i = 0; i < n; i++) { const [sx, sy] = at(i); if (Math.abs(x - sx) <= BOX_W / 2 && Math.abs(y - sy) <= BOX_H / 2) return i; }
  return -1;
}
/** A chip with an amount beside it (bets, the pot). */
export function chip(g, x, y, amt) {
  g.fillStyle = '#d62b2b'; g.beginPath(); g.arc(x, y, 6, 0, 7); g.fill(); g.strokeStyle = INK; g.lineWidth = 1.5; g.stroke();
  g.fillStyle = '#fffdf6'; g.beginPath(); g.arc(x, y, 2.5, 0, 7); g.fill();
  outlined(g, String(amt), x + 9, y, 'left');
}
/** Light text with a dark outline (readable on the felt). */
export function outlined(g, text, x, y, align = 'center', color = CREAM) {
  g.font = 'bold 12px system-ui'; g.textAlign = align; g.textBaseline = 'middle';
  g.lineWidth = 3; g.strokeStyle = INK; g.strokeText(text, x, y); g.fillStyle = color; g.fillText(text, x, y);
}
/** An element (tag, class, text). */
export const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
